import React, { useRef, useState } from 'react';

import emotionStyled from '@emotion/styled';
import styled, { css } from 'styled-components';
import {
  commentEditorStyles,
  commentEditorStyles2,
  commentPlaceholderStyles,
  commentPlaceholderStyles2,
} from 'styles/common';
import { checkFeedbackModuleEnabled } from 'utils/helpers/moduleAccess';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';

import { PostField } from 'uiToolkitV1/PostField/PostField';

// const emojiPlugin = createNewEmojiPlugin({ size: 'small' });

export default function EditCommentComponent({
  editorState,
  setEditorState,
  onEnter,
  loading,
  uploadedFile,
  setUploadedFile,
  onEscape,
  MediaPreview,
  UploadTypesView,
  VisibilityComponent,
  TagRecommendations,
  placeholder,
  className,
  handleBlur = null,
  SentimentComponent = null,
  KudosComponent = null,
  enableKudos = true,
}) {
  const postFieldContainerRef = useRef(null);

  const allowBlurRef = useRef({ allowBlurEvent: true });

  const [emojiPlugin, setEmojiPlugin] = useState(null);

  const showKudosToggle = !!KudosComponent && checkFeedbackModuleEnabled() && enableKudos;

  // onMouseDown fires before onBlur
  // this makes it possible to not fire on blur when clicking on any element under PostFieldContainer
  // such as adding emojis or changing comment visibility
  const onBlurHandler = (e) => {
    if (allowBlurRef.current.allowBlurEvent) {
      handleBlur && handleBlur(e);
    }
    allowBlurRef.current.allowBlurEvent = true;
    return e;
  };

  const onMouseDownHandler = (e) => {
    if (postFieldContainerRef.current.contains(e.target)) {
      allowBlurRef.current.allowBlurEvent = false;
    } else {
      allowBlurRef.current.allowBlurEvent = true;
    }
    return e;
  };

  return (
    <div className={className}>
      <PostFieldContainer loading={loading} ref={postFieldContainerRef}>
        <PostField
          placeholder={placeholder && placeholder !== '' ? placeholder : 'Enter Comment'}
          // height="2rem"
          updateEditorState={setEditorState}
          editorState={editorState}
          usePortalForSuggestions
          editorStyles={commentEditorStyles}
          placeholderStyles={commentPlaceholderStyles}
          useMoneyFunctionality
          useMentionFunctionality
          onEnter={onEnter}
          widgetType={SEARCH_WIDGET_TYPES.comment}
          createNewTagFunctionality={false}
          onEscape={onEscape}
          useEmojiFunctionality
          allowNewLine
          handleBlur={onBlurHandler}
          setEmojiPlugin={setEmojiPlugin}
          portalParentRef={postFieldContainerRef}
        />
        {MediaPreview}
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }} onMouseDown={onMouseDownHandler}>
          {Boolean(emojiPlugin) && (
            <>{React.cloneElement(UploadTypesView, { EmojiSelect: emojiPlugin?.EmojiSelect })}</>
          )}
          {showKudosToggle ? <VerticalDivider /> : null}
          {showKudosToggle && KudosComponent}
          {/* {VisibilityComponent}
          {SentimentComponent} */}
          {TagRecommendations}
        </div>
      </PostFieldContainer>
    </div>
  );
}

export function EditCommentComponentV2({
  editorState,
  setEditorState,
  onEnter,
  loading,
  uploadedFile,
  setUploadedFile,
  onEscape,
  MediaPreview,
  UploadTypesView,
  VisibilityComponent,
  TagRecommendations,
  placeholder,
  className,
  handleBlur = null,
  onFocus,
  SentimentComponent = null,
  KudosComponent = null,
  height,
  enableKudos = true,
}) {
  const postFieldContainerRef = useRef(null);

  const allowBlurRef = useRef({ allowBlurEvent: true });

  const [emojiPlugin, setEmojiPlugin] = useState(null);

  const showKudosToggle = !!KudosComponent && checkFeedbackModuleEnabled() && enableKudos;

  // onMouseDown fires before onBlur
  // this makes it possible to not fire on blur when clicking on any element under PostFieldContainer
  // such as adding emojis or changing comment visibility
  const onBlurHandler = (e) => {
    if (allowBlurRef.current.allowBlurEvent) {
      handleBlur && handleBlur(e);
    }
    allowBlurRef.current.allowBlurEvent = true;
    return e;
  };

  const onMouseDownHandler = (e) => {
    // will only have effect on first click
    onFocus && onFocus();
    if (postFieldContainerRef.current.contains(e.target)) {
      allowBlurRef.current.allowBlurEvent = false;
    } else {
      allowBlurRef.current.allowBlurEvent = true;
    }
    return e;
  };

  return (
    <div className={className}>
      <PostFieldContainerV2 loading={loading} ref={postFieldContainerRef}>
        <div onMouseDown={onMouseDownHandler}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <PostField
              placeholder={placeholder && placeholder !== '' ? placeholder : 'Enter Comment'}
              updateEditorState={setEditorState}
              editorState={editorState}
              usePortalForSuggestions
              editorStyles={commentEditorStyles2}
              placeholderStyles={commentPlaceholderStyles2}
              useMoneyFunctionality
              useMentionFunctionality
              onEnter={onEnter}
              widgetType={SEARCH_WIDGET_TYPES.comment}
              createNewTagFunctionality={false}
              onEscape={onEscape}
              useEmojiFunctionality
              allowNewLine
              handleBlur={onBlurHandler}
              setEmojiPlugin={setEmojiPlugin}
              portalParentRef={postFieldContainerRef}
            />

            {MediaPreview}
            <EditCommentActionsWrapper
              style={{
                position: 'relative', // <- for emoji plugin to attach
              }}
            >
              {showKudosToggle && KudosComponent}

              {showKudosToggle && <VerticalDivider />}
              {Boolean(emojiPlugin) && (
                <>
                  {/* CLONE ELEMENT IS NOT PREFERRED PLEASE REFER: https://react.dev/reference/react/cloneElement#alternatives */}
                  {/* {React.cloneElement(UploadTypesView, { EmojiSelect: emojiPlugin?.EmojiSelect })} */}
                  <UploadTypesView EmojiSelect={emojiPlugin?.EmojiSelect} />
                </>
              )}
            </EditCommentActionsWrapper>
          </div>
          {TagRecommendations}
        </div>
      </PostFieldContainerV2>
    </div>
  );
}

const PostFieldContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  border-radius: 4px;
  ${(props) =>
    props.loading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const PostFieldContainerV2 = emotionStyled.div`
  background: ${(props) => props.theme.colors.neutral.F2F2F2};
  border-radius: 4px;
  ${(props) =>
    props.loading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const EditCommentActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: 16px;
`;

const PostFieldWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

const VerticalDivider = emotionStyled.span`
  height: 1rem;
  width: 0px;
  border-right: 1px solid ${(props) => props.theme.colors.neutral.CFD7DF};
  margin-left: 0.5rem;
  margin-right: 0.5rem;
`;
